import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { 
  RefreshCw, 
  Download, 
  CheckCircle, 
  AlertCircle,
  Loader2,
  Database,
  Calendar,
  Image,
  DollarSign,
  Info
} from 'lucide-react';

const AdminSync = () => {
  const [syncStatus, setSyncStatus] = useState('idle'); // idle, syncing, success, error
  const [syncProgress, setSyncProgress] = useState({
    current: 0,
    total: 0,
    step: ''
  });
  const [syncResult, setSyncResult] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const [checkResult, setCheckResult] = useState(null);
  const [isChecking, setIsChecking] = useState(false);

  // Charger la dernière synchronisation
  useEffect(() => {
    loadLastSync();
  }, []);

  const loadLastSync = async () => {
    try {
      // TODO: Implémenter l'endpoint pour récupérer la dernière sync
      const response = await fetch('/api/sync/status');
      if (response.ok) {
        const data = await response.json();
        setLastSync(data);
      }
    } catch (error) {
      console.error('Error loading last sync:', error);
    }
  };

  const handleFullSync = async () => {
    setSyncStatus('syncing');
    setSyncProgress({ current: 0, total: 0, step: 'Initialisation...' });
    
    try {
      // Étape 1: Authentification
      setSyncProgress({ current: 0, total: 5, step: 'Authentification Villanovo...' });
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulation
      
      // Étape 2: Récupération de la liste
      setSyncProgress({ current: 1, total: 5, step: 'Récupération de la liste des villas...' });
      const response = await fetch('/api/sync/villas', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) throw new Error('Sync failed');
      
      const data = await response.json();
      
      // Étape 3: Import des détails
      setSyncProgress({ current: 2, total: 5, step: 'Import des détails...' });
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Étape 4: Import des photos
      setSyncProgress({ current: 3, total: 5, step: 'Import des photos...' });
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Étape 5: Finalisation
      setSyncProgress({ current: 4, total: 5, step: 'Finalisation...' });
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setSyncProgress({ current: 5, total: 5, step: 'Terminé !' });
      setSyncStatus('success');
      setSyncResult(data);
      loadLastSync();
      
    } catch (error) {
      console.error('Sync error:', error);
      setSyncStatus('error');
      setSyncResult({ error: error.message });
    }
  };

  const handleCheckDifferences = async () => {
    setIsChecking(true);
    setCheckResult(null);
    
    try {
      const response = await fetch('/api/sync/check', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) throw new Error('Check failed');
      
      const data = await response.json();
      setCheckResult(data.data);
      
    } catch (error) {
      console.error('Check error:', error);
      setCheckResult({ error: error.message });
    } finally {
      setIsChecking(false);
    }
  };

  const handleApplyChanges = async () => {
    setSyncStatus('syncing');
    setSyncProgress({ current: 0, total: 3, step: 'Application des changements...' });
    
    try {
      const response = await fetch('/api/sync/apply-changes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) throw new Error('Apply changes failed');
      
      const data = await response.json();
      setSyncStatus('success');
      setSyncResult(data.data);
      setCheckResult(null); // Reset check result
      loadLastSync();
      
    } catch (error) {
      console.error('Apply changes error:', error);
      setSyncStatus('error');
      setSyncResult({ error: error.message });
    }
  };

  const handleQuickSync = async () => {
    setSyncStatus('syncing');
    setSyncProgress({ current: 0, total: 2, step: 'Synchronisation rapide...' });
    
    try {
      const response = await fetch('/api/sync/quick', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) throw new Error('Quick sync failed');
      
      const data = await response.json();
      setSyncProgress({ current: 2, total: 2, step: 'Terminé !' });
      setSyncStatus('success');
      setSyncResult(data);
      loadLastSync();
      
    } catch (error) {
      console.error('Quick sync error:', error);
      setSyncStatus('error');
      setSyncResult({ error: error.message });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Synchronisation Villanovo</h2>
        <p className="text-gray-600 mt-2">
          Importez et mettez à jour les villas depuis l'API Villanovo
        </p>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <Database className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">État de la Base</h3>
              <p className="text-sm text-gray-600">Villas actuellement en base</p>
            </div>
          </div>
          <p className="text-3xl font-bold text-gray-900">{lastSync?.totalVillas || 0}</p>
          <p className="text-sm text-gray-500 mt-1">villas actives</p>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Dernière Synchronisation</h3>
              <p className="text-sm text-gray-600">Date de la dernière mise à jour</p>
            </div>
          </div>
          <p className="text-lg font-semibold text-gray-900">
            {lastSync?.lastSyncDate 
              ? new Date(lastSync.lastSyncDate).toLocaleDateString('fr-FR', {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })
              : 'Jamais synchronisé'
            }
          </p>
        </Card>
      </div>

      {/* Sync Actions */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions de Synchronisation</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Full Sync */}
          <div className="border-2 border-gray-200 rounded-lg p-4">
            <div className="flex items-start gap-3 mb-3">
              <Download className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-semibold text-gray-900">Synchronisation Complète</h4>
                <p className="text-sm text-gray-600 mt-1">
                  Importe toutes les villas, photos, tarifs et équipements depuis Villanovo
                </p>
              </div>
            </div>
            <Button
              onClick={handleFullSync}
              disabled={syncStatus === 'syncing'}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
            >
              {syncStatus === 'syncing' ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Synchronisation...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Synchronisation Complète
                </>
              )}
            </Button>
            <p className="text-xs text-gray-500 mt-2">⏱️ Durée estimée: 10-30 minutes</p>
          </div>

          {/* Check Differences */}
          <div className="border-2 border-amber-200 rounded-lg p-4 bg-gradient-to-br from-amber-50 to-orange-50">
            <div className="flex items-start gap-3 mb-3">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <h4 className="font-semibold text-gray-900">Vérification Intelligente</h4>
                <p className="text-sm text-gray-600 mt-1">
                  Compare l'API avec la BDD pour détecter les nouvelles villas et celles à désactiver
                </p>
              </div>
            </div>
            <Button
              onClick={handleCheckDifferences}
              disabled={isChecking || syncStatus === 'syncing'}
              variant="outline"
              className="w-full border-amber-600 text-amber-700 hover:bg-amber-100"
            >
              {isChecking ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Vérification...
                </>
              ) : (
                <>
                  <AlertCircle className="w-4 h-4 mr-2" />
                  Vérifier les Différences
                </>
              )}
            </Button>
            <p className="text-xs text-gray-500 mt-2">⏱️ Durée estimée: 10-30 secondes</p>
          </div>

          {/* Quick Sync */}
          <div className="border-2 border-gray-200 rounded-lg p-4">
            <div className="flex items-start gap-3 mb-3">
              <RefreshCw className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <h4 className="font-semibold text-gray-900">Synchronisation Rapide</h4>
                <p className="text-sm text-gray-600 mt-1">
                  Met à jour uniquement les villas modifiées récemment
                </p>
              </div>
            </div>
            <Button
              onClick={handleQuickSync}
              disabled={syncStatus === 'syncing'}
              variant="outline"
              className="w-full border-green-600 text-green-700 hover:bg-green-50"
            >
              {syncStatus === 'syncing' ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Synchronisation...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Synchronisation Rapide
                </>
              )}
            </Button>
            <p className="text-xs text-gray-500 mt-2">⏱️ Durée estimée: 1-5 minutes</p>
          </div>
        </div>

        {/* Check Results */}
        {checkResult && !checkResult.error && (
          <Card className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-blue-600" />
              Résultats de la Vérification
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-white rounded-lg p-4 border border-blue-200">
                <p className="text-sm text-gray-600 mb-1">Nouvelles Villas</p>
                <p className="text-3xl font-bold text-green-600">{checkResult.differences?.newVillas?.count || 0}</p>
                <p className="text-xs text-gray-500 mt-1">À créer dans la BDD</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-blue-200">
                <p className="text-sm text-gray-600 mb-1">Villas à Désactiver</p>
                <p className="text-3xl font-bold text-red-600">{checkResult.differences?.toDeactivate?.count || 0}</p>
                <p className="text-xs text-gray-500 mt-1">Plus dans l'API</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-blue-200">
                <p className="text-sm text-gray-600 mb-1">Villas à Réactiver</p>
                <p className="text-3xl font-bold text-blue-600">{checkResult.differences?.toReactivate?.count || 0}</p>
                <p className="text-xs text-gray-500 mt-1">De retour dans l'API</p>
              </div>
            </div>

            {/* Show samples if there are differences */}
            {(checkResult.differences?.newVillas?.count > 0 || checkResult.differences?.toDeactivate?.count > 0 || checkResult.differences?.toReactivate?.count > 0) && (
              <>
                {checkResult.differences?.newVillas?.count > 0 && (
                  <div className="mb-4">
                    <p className="text-sm font-semibold text-gray-700 mb-2">
                      📋 Échantillon des nouvelles villas ({Math.min(10, checkResult.differences.newVillas.count)} sur {checkResult.differences.newVillas.count}):
                    </p>
                    <div className="bg-white rounded-lg p-3 text-xs font-mono max-h-32 overflow-auto border">
                      {checkResult.differences.newVillas.sample?.map((v, i) => (
                        <div key={i} className="py-1 border-b border-gray-100 last:border-0">
                          #{v.id} - {v.name} ({v.zone})
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {checkResult.differences?.toDeactivate?.count > 0 && (
                  <div className="mb-4">
                    <p className="text-sm font-semibold text-gray-700 mb-2">
                      ❌ Échantillon des villas à désactiver ({Math.min(10, checkResult.differences.toDeactivate.count)} sur {checkResult.differences.toDeactivate.count}):
                    </p>
                    <div className="bg-white rounded-lg p-3 text-xs font-mono max-h-32 overflow-auto border">
                      {checkResult.differences.toDeactivate.sample?.map((v, i) => (
                        <div key={i} className="py-1 border-b border-gray-100 last:border-0">
                          #{v.id} - {v.name}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <Button
                  onClick={handleApplyChanges}
                  disabled={syncStatus === 'syncing'}
                  className="w-full bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800"
                >
                  {syncStatus === 'syncing' ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Application...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Appliquer les Changements
                    </>
                  )}
                </Button>
              </>
            )}

            {(checkResult.differences?.newVillas?.count === 0 && checkResult.differences?.toDeactivate?.count === 0 && checkResult.differences?.toReactivate?.count === 0) && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                <p className="text-sm text-green-800">
                  <strong>Tout est à jour !</strong> Aucune différence détectée entre l'API et la base de données.
                </p>
              </div>
            )}
          </Card>
        )}
      </Card>

      {/* Progress */}
      {syncStatus === 'syncing' && (
        <Card className="p-6 bg-blue-50 border-blue-200">
          <div className="flex items-center gap-3 mb-4">
            <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
            <div>
              <h3 className="font-semibold text-blue-900">Synchronisation en cours...</h3>
              <p className="text-sm text-blue-700">{syncProgress.step}</p>
            </div>
          </div>
          <div className="w-full bg-blue-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(syncProgress.current / syncProgress.total) * 100}%` }}
            />
          </div>
          <p className="text-sm text-blue-700 mt-2">
            Étape {syncProgress.current}/{syncProgress.total}
          </p>
        </Card>
      )}

      {/* Success Result */}
      {syncStatus === 'success' && syncResult && (
        <Card className="p-6 bg-green-50 border-green-200">
          <div className="flex items-center gap-3 mb-4">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <h3 className="font-semibold text-green-900">Synchronisation réussie !</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-green-700 font-medium">{syncResult.imported || 0}</p>
              <p className="text-green-600">Villas importées</p>
            </div>
            <div>
              <p className="text-green-700 font-medium">{syncResult.updated || 0}</p>
              <p className="text-green-600">Villas mises à jour</p>
            </div>
            <div>
              <p className="text-green-700 font-medium">{syncResult.photos || 0}</p>
              <p className="text-green-600">Photos</p>
            </div>
            <div>
              <p className="text-green-700 font-medium">{syncResult.duration || '0'}s</p>
              <p className="text-green-600">Durée</p>
            </div>
          </div>
        </Card>
      )}

      {/* Error Result */}
      {syncStatus === 'error' && syncResult && (
        <Card className="p-6 bg-red-50 border-red-200">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <div>
              <h3 className="font-semibold text-red-900">Erreur de synchronisation</h3>
              <p className="text-sm text-red-700 mt-1">{syncResult.error}</p>
            </div>
          </div>
        </Card>
      )}

      {/* Info Box */}
      <Card className="p-4 bg-gray-50 border-gray-200">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-gray-600 mt-0.5" />
          <div className="text-sm text-gray-700">
            <p className="font-medium mb-2">À propos de la synchronisation</p>
            <ul className="space-y-1 text-xs">
              <li>• La synchronisation complète importe toutes les villas depuis l'API Villanovo</li>
              <li>• Les données incluent: détails, photos, tarifs, équipements et disponibilités</li>
              <li>• La synchronisation rapide ne met à jour que les villas modifiées récemment</li>
              <li>• Les disponibilités (calendriers) sont mises à jour automatiquement à chaque consultation</li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default AdminSync;

